/**
 * As chamadas da tela à API do SGR (spec 032). A busca e a escolha são POST
 * (a placa e o CPF não passam pela URL) e NUNCA lançam: erro de rede, 404 da
 * API anterior (rollback) ou 403 viram `unavailable` — e a tela cai em "preencha
 * a ficha à mão".
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type {
  EndCustomerDetail,
  SgrPickResponse,
  SgrRefreshResponse,
  SgrRoutineStatus,
  SgrSalePointLinkPreview,
  SgrSalePointList,
  SgrSearchBody,
  SgrSearchResponse,
  SgrSettings,
  SgrStatus,
  SheetSgrRefreshResponse,
  TicketSearchResult,
} from '@erp/shared';

/** a chave das consultas da integração (o nome do sistema não vai em texto do bundle) */
const SGR_KEY = ['erp', 'source-integration'] as const;

export const sgrStatusQuery = () =>
  queryOptions({
    queryKey: [...SGR_KEY, 'status'] as const,
    queryFn: async ({ signal }): Promise<SgrStatus | null> => {
      const res = await api<SgrStatus>('/sgr/status', { signal });
      // API anterior à 032 (404) ou sem acesso: a integração não existe para a tela
      return res.error ? null : res.data;
    },
    staleTime: 60_000,
  });

/** a integração está disponível NESTE tenant (senão a tela não mostra a busca) */
export function useSgr(): { available: boolean; sourceName: string; status: SgrStatus | null } {
  const q = useQuery(sgrStatusQuery());
  const status = q.data ?? null;
  return {
    available: !!status && status.available && status.enabled,
    sourceName: status?.sourceName ?? '',
    status,
  };
}

export async function sgrSearch(body: SgrSearchBody, signal: AbortSignal): Promise<SgrSearchResponse> {
  const res = await api<SgrSearchResponse>('/sgr/vehicles/search', { method: 'POST', body, signal });
  if (res.error) return { status: 'unavailable', reason: 'network_error' };
  return res.data;
}

export async function sgrPick(sgrId: string): Promise<SgrPickResponse> {
  const res = await api<SgrPickResponse>(`/sgr/vehicles/${encodeURIComponent(sgrId)}/pick`, {
    method: 'POST',
    body: {},
    timeoutMs: 45_000,
  });
  if (res.error) return { status: 'unavailable', reason: 'network_error' };
  return res.data;
}

/** o recurso do ERP de um veículo do SGR, sem ler o SGR; null = ainda não está no ERP */
export async function sgrResourceOf(sgrId: string): Promise<number | null> {
  const res = await api<{ equipmentId: number }>(`/sgr/vehicles/${encodeURIComponent(sgrId)}/resource`);
  return res.error ? null : res.data.equipmentId;
}

export const endCustomerDetailQuery = (id: number) =>
  queryOptions({
    queryKey: [...SGR_KEY, 'end-customer', id] as const,
    queryFn: async ({ signal }) => {
      const res = await api<EndCustomerDetail>(`/end-customers/${id}`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });

export const vehicleTicketsQuery = (kind: 'by-resource' | 'by-end-customer', id: number) =>
  queryOptions({
    queryKey: ['erp', 'tickets', kind, id] as const,
    queryFn: async ({ signal }) => {
      const res = await api<TicketSearchResult>(`/tickets/${kind}/${id}`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });

/** "Atualizar do SGR" da página do veículo ou do associado */
export function useSgrPageRefresh(kind: 'resources' | 'end-customers', id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<SgrRefreshResponse> => {
      const res = await api<SgrRefreshResponse>(`/${kind}/${id}/sgr-refresh`, { method: 'POST', timeoutMs: 45_000 });
      if (res.error) return { status: 'unavailable', reason: 'network_error' };
      return res.data;
    },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['erp', 'resources'] }),
        qc.invalidateQueries({ queryKey: [...SGR_KEY, 'end-customer'] }),
      ]),
  });
}

/** "Atualizar do SGR" do cartão da ficha */
export function useSheetSgrRefresh(ticketNumber: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<SheetSgrRefreshResponse> => {
      const res = await api<SheetSgrRefreshResponse>(`/tickets/${ticketNumber}/sheet/sgr-refresh`, {
        method: 'POST',
        timeoutMs: 45_000,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['erp', 'tickets'] }),
  });
}

// ── pontos de venda (§8) ────────────────────────────────────────────────────

export const salePointsQuery = (pending: boolean) =>
  queryOptions({
    queryKey: [...SGR_KEY, 'sale-points', { pending }] as const,
    queryFn: async ({ signal }) => {
      const res = await api<SgrSalePointList>('/sgr/sale-points', { query: { pending }, signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });

export const salePointPreviewQuery = (id: number, clientId: number | null) =>
  queryOptions({
    queryKey: [...SGR_KEY, 'sale-point-preview', id, clientId] as const,
    queryFn: async ({ signal }) => {
      const res = await api<SgrSalePointLinkPreview>(`/sgr/sale-points/${id}/link-preview`, {
        query: { client_id: clientId ?? undefined },
        signal,
      });
      if (res.error) throw res.error;
      return res.data;
    },
  });

/**
 * A rotina noturna do Recurso (spec 035), só admin: quem não é recebe 403 e o
 * bloco não aparece (null). Enquanto roda, pergunta de 5 em 5 s.
 */
export const sgrRoutineQuery = () =>
  queryOptions({
    queryKey: [...SGR_KEY, 'routine'] as const,
    queryFn: async ({ signal }): Promise<SgrRoutineStatus | null> => {
      const res = await api<SgrRoutineStatus>('/sgr/routine', { signal });
      return res.error ? null : res.data;
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.available && data.lastRun?.status === 'running' ? 5_000 : false;
    },
  });

export function useStartSgrRoutine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api<{ runId: number }>('/sgr/routine', { method: 'POST' });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [...SGR_KEY, 'routine'] }),
  });
}

export function useSyncSalePoints() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api<{ status: 'ok'; count: number } | { status: 'unavailable'; reason: string }>('/sgr/sale-points/sync', {
        method: 'POST',
        timeoutMs: 45_000,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [...SGR_KEY, 'sale-points'] }),
  });
}

export function useLinkSalePoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: number; clientId: number | null }) => {
      const res = await api<{ moved: number }>(`/sgr/sale-points/${input.id}/link`, {
        method: 'POST',
        body: { clientId: input.clientId },
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: [...SGR_KEY] }),
        qc.invalidateQueries({ queryKey: ['erp', 'resources'] }),
      ]),
  });
}

export const sgrSettingsQuery = () =>
  queryOptions({
    queryKey: [...SGR_KEY, 'settings'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<SgrSettings>('/sgr/settings', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });

export function useSetDirectSaleClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (directSaleClientId: number | null) => {
      const res = await api<SgrSettings>('/sgr/settings', { method: 'PUT', body: { directSaleClientId } });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [...SGR_KEY] }),
  });
}
